export interface PreviewView {
  scale: number;
  x: number;
  y: number;
}

export const PREVIEW_MIN_SCALE = 1;
export const PREVIEW_MAX_SCALE = 4;
export const PREVIEW_SCALE_STEP = 0.5;
export const PREVIEW_DOUBLE_CLICK_SCALE = 2;

export const PREVIEW_RESET_VIEW: PreviewView = { scale: 1, x: 0, y: 0 };

export interface Size {
  width: number;
  height: number;
}

function clampAxis(offset: number, imageSize: number, containerSize: number, scale: number) {
  const limit = Math.max(0, (imageSize * scale - containerSize) / 2);
  if (limit === 0) {
    return 0;
  }
  return Math.min(limit, Math.max(-limit, offset));
}

/**
 * Keeps a zoomed image covering the frame: it can be dragged until its edge
 * reaches the frame edge, and an axis that still fits stays centred.
 */
export function clampPreviewView(view: PreviewView, image: Size, container: Size): PreviewView {
  const scale = Math.min(PREVIEW_MAX_SCALE, Math.max(PREVIEW_MIN_SCALE, view.scale));
  return {
    scale,
    x: clampAxis(view.x, image.width, container.width, scale),
    y: clampAxis(view.y, image.height, container.height, scale),
  };
}

/**
 * Zooms to `nextScale` keeping the image point under `point` where it is.
 * `point` is relative to the frame centre; the image is scaled about its own
 * centre, which sits at the frame centre plus the current offset.
 */
export function zoomPreviewAt(
  view: PreviewView,
  nextScale: number,
  point: { x: number; y: number },
  image: Size,
  container: Size,
): PreviewView {
  const scale = Math.min(PREVIEW_MAX_SCALE, Math.max(PREVIEW_MIN_SCALE, nextScale));
  const ratio = scale / view.scale;
  return clampPreviewView(
    {
      scale,
      x: point.x - (point.x - view.x) * ratio,
      y: point.y - (point.y - view.y) * ratio,
    },
    image,
    container,
  );
}
